import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChildrenEditor } from "@/components/ChildrenEditor";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  ["street", "Strasse & Nr.", "text"],
  ["postal_code", "PLZ", "text"],
  ["city", "Ort", "text"],
  ["birth_date", "Geburtsdatum", "date"],
  ["nationality", "Nationalität", "text"],
  ["ahv_number", "AHV-Nummer (756.xxxx.xxxx.xx)", "text"],
  ["iban", "IBAN (für Lohnzahlung)", "text"],
  ["phone", "Mobilnummer", "text"],
  ["email", "E-Mail", "email"],
  ["permit_expiry", "Ablaufdatum Bewilligung (falls vorhanden)", "date"],
];

export default function EmployeeOnboarding() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/employee-onboarding/${token}`)
      .then((r) => {
        setInfo(r.data);
        const pre = {};
        Object.entries(r.data.prefill || {}).forEach(([k, v]) => { if (v != null) pre[k] = Array.isArray(v) ? v : String(v); });
        setForm(pre);
      })
      .catch((e) => setError(e.response?.data?.detail || "Link ungültig oder abgelaufen."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { ...form };
      payload.children = (form.children || []).filter((c) => c.birth_date);
      payload.children_count = payload.children.length;
      await axios.post(`${API}/public/employee-onboarding/${token}`, payload);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="onboarding-page">
      <div className="max-w-lg mx-auto">
        <Card className="p-6 border border-slate-200 bg-white shadow-sm">
          {done ? (
            <div className="text-center py-8" data-testid="onboarding-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
              <h1 className="text-xl font-semibold text-slate-900">Vielen Dank!</h1>
              <p className="text-sm text-slate-500 mt-2">Deine Angaben wurden sicher an die Lohnbuchhaltung übermittelt.</p>
            </div>
          ) : !info ? (
            <p className="text-sm text-rose-600" data-testid="onboarding-error">{error || "Lade…"}</p>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-slate-900">Hallo {info.first_name}! 👋</h1>
              <p className="text-sm text-slate-500 mt-1 mb-5">
                Bitte fülle deine Personalien für die Lohnbuchhaltung von <b>{info.company_name}</b> aus.
                Alle Angaben werden vertraulich behandelt.
              </p>
              <form onSubmit={submit} className="space-y-3">
                {FIELDS.map(([key, label, type]) => (
                  <div key={key} className="space-y-1">
                    <Label htmlFor={`ob-${key}`}>{label}</Label>
                    <Input id={`ob-${key}`} type={type} value={form[key] || ""}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      data-testid={`onboarding-${key}-input`} />
                  </div>
                ))}
                <div className="space-y-1">
                  <Label>Zivilstand</Label>
                  <select className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm" value={form.civil_status || ""}
                    onChange={(e) => setForm({ ...form, civil_status: e.target.value })} data-testid="onboarding-civil-status">
                    <option value="">wählen…</option>
                    {["ledig", "verheiratet", "geschieden", "verwitwet", "eingetragene Partnerschaft"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <ChildrenEditor value={form.children || []} onChange={(children) => setForm({ ...form, children })} testIdPrefix="onboarding-children" />
                <div className="space-y-1">
                  <Label>Aufenthaltsbewilligung</Label>
                  <select className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm" value={form.residence_permit || ""}
                    onChange={(e) => setForm({ ...form, residence_permit: e.target.value })} data-testid="onboarding-permit">
                    <option value="">wählen…</option>
                    {["Schweizer/in (keine)", "C (Niederlassung)", "B (Aufenthalt)", "L (Kurzaufenthalt)", "G (Grenzgänger)"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                {error && <p className="text-sm text-rose-600">{error}</p>}
                <Button type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="onboarding-submit">
                  {busy ? "Speichert…" : "Angaben absenden"}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
